import { useTranslation } from "react-i18next";
import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { Button } from "../ui/button";

// Helper function to calculate page numbers and ellipsis
const getPaginationItems = (
  totalPages: number,
  currentPage: number,
  maxVisible: number = 4,
): (number | string)[] => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const items: (number | string)[] = [];

  if (currentPage <= maxVisible - 1) {
    // Show first maxVisible-1 pages, ellipsis, last page
    for (let i = 1; i <= maxVisible - 1; i++) {
      items.push(i);
    }
    items.push("...");
    items.push(totalPages);
  } else if (currentPage >= totalPages - (maxVisible - 2)) {
    // Show first page, ellipsis, last maxVisible-1 pages
    items.push(1);
    items.push("...");
    for (let i = totalPages - (maxVisible - 2); i <= totalPages; i++) {
      items.push(i);
    }
  } else {
    // Show first page, ellipsis, currentPage-1, currentPage, currentPage+1, ellipsis, last page
    items.push(1);
    items.push("...");
    items.push(currentPage - 1);
    items.push(currentPage);
    items.push(currentPage + 1);
    items.push("...");
    items.push(totalPages);
  }

  return items;
};

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  onPageChange,
  hasNextPage,
  hasPreviousPage,
}) => {
  const { t } = useTranslation("common");
  const paginationItems = getPaginationItems(totalPages, currentPage);
  const isFirstPage =
    hasPreviousPage !== undefined ? !hasPreviousPage : currentPage === 1;
  const isLastPage =
    hasNextPage !== undefined ? !hasNextPage : currentPage === totalPages;

  // Handlers
  const handlePrev = () => {
    if (!isFirstPage) onPageChange(currentPage - 1);
  };
  const handleNext = () => {
    if (!isLastPage) onPageChange(currentPage + 1);
  };

  return (
    <nav
      className="inline-flex items-center gap-2"
      aria-label={t("ui_kit.pagination")}
    >
      {/* Previous Button */}
      <Button
        variant="outline"
        className="lg:w-9 w-6 h-6 lg:h-9 p-0 rounded-xs border text-sm flex items-center justify-center"
        onClick={handlePrev}
        disabled={isFirstPage}
        aria-label={t("ui_kit.previous_page")}
      >
        {/* Direction is physical: in RTL "previous" points the other way. */}
        <ChevronLeftIcon className="w-4 h-4 rtl:rotate-180" />
      </Button>

      {/* Page Number Buttons and Ellipsis */}
      {paginationItems.map((item, idx) =>
        typeof item === "number" ? (
          <Button
            key={item}
            variant={"outline"}
            className={`lg:w-9 w-6 h-6 lg:h-9 p-0  border-gray-300 rounded-xs border text-xs lg:text-sm flex items-center justify-center ${
              item === currentPage
                ? "text-white! bg-primary! "
                : " text-muted-foreground! bg-white!"
            }`}
            onClick={() => onPageChange(item)}
          >
            {item}
          </Button>
        ) : (
          <span
            key={`ellipsis-${idx}`}
            className="px-3 py-1 text-muted-foreground text-sm select-none"
          >
            ...
          </span>
        ),
      )}

      {/* Next Button */}
      <Button
        variant="outline"
        className="lg:w-9 w-6 h-6 lg:h-9 p-0 rounded-xs border text-sm flex items-center justify-center"
        onClick={handleNext}
        disabled={isLastPage}
        aria-label={t("ui_kit.next_page")}
      >
        <ChevronRightIcon className="w-4 h-4 rtl:rotate-180" />
      </Button>
    </nav>
  );
};
